import React from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Stack,
  useTheme,
} from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/**
 * Enterprise-grade PageHeader component
 * Provides consistent page layout with title, breadcrumbs, and action buttons
 *
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.subtitle - Optional subtitle/description
 * @param {Array} props.breadcrumbs - Array of breadcrumb objects [{label, path}]
 * @param {React.ReactNode} props.actions - Action buttons/components
 * @param {React.ReactNode} props.tabs - Optional tabs component
 * @param {React.ReactNode} props.filters - Optional filter components
 */
const PageHeader = ({
  title,
  subtitle,
  breadcrumbs = [],
  actions,
  tabs,
  filters,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleBreadcrumbClick = (event, crumb) => {
    event.preventDefault();
    // A crumb may supply its own handler instead of a plain path, so a page
    // that must intercept navigation (e.g. confirming unsaved changes before
    // leaving an editor) can do so. Falls back to `path` for every existing
    // caller.
    if (typeof crumb.onClick === 'function') {
      crumb.onClick(event);
      return;
    }
    if (crumb.path) navigate(crumb.path, crumb.state ? { state: crumb.state } : undefined);
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <Box
          sx={{
            px: 3,
            pt: 2,
            pb: 1,
          }}
        >
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{
              '& .MuiBreadcrumbs-separator': {
                mx: 0.5,
              },
            }}
          >
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              return isLast ? (
                <Typography
                  key={crumb.label}
                  variant="body2"
                  color="text.primary"
                  sx={{ fontWeight: 500 }}
                >
                  {crumb.label}
                </Typography>
              ) : (
                <Link
                  key={crumb.label}
                  component="button"
                  variant="body2"
                  onClick={(e) => handleBreadcrumbClick(e, crumb)}
                  sx={{
                    color: 'text.secondary',
                    textDecoration: 'none',
                    '&:hover': {
                      color: 'primary.main',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {crumb.label}
                </Link>
              );
            })}
          </Breadcrumbs>
        </Box>
      )}

      {/* Title and Actions
       *  flexWrap='wrap' lets the actions row drop below the title when
       *  the viewport is too narrow for both — without this the title
       *  shrank to a 3-column-wide stripe (Kubernetes/Monitoring/lab-…
       *  cluster wrapping vertically).
       */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: subtitle ? 0.5 : 0,
              color: 'text.primary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={typeof title === 'string' ? title : undefined}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 800 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1 }}
          >
            {actions}
          </Stack>
        )}
      </Box>

      {/* Tabs */}
      {tabs && (
        <Box sx={{ px: 3 }}>
          {tabs}
        </Box>
      )}

      {/* Filters */}
      {filters && (
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          {filters}
        </Box>
      )}
    </Box>
  );
};

export default PageHeader;
